import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, SectionHeading } from "./shared";
import { TIMELINE, IMAGES } from "../../data";

export default function Incident() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const lineScale = useTransform(scrollYProgress, [0.05, 0.7], [0, 1]);

  return (
    <section id="incident" ref={ref} className="relative py-28 md:py-40 bg-[#0f1724]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <SectionHeading
          chapter="01 // THE INCIDENT"
          title={<>03:14:02 — The<br />sixteen seconds</>}
          kicker="From the DCS historian, field logs and the emergency sequence, Asritas reconstructs the sixteen seconds exactly as they unfolded — the first step in tracing the failure back to its origin."
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Timeline */}
          <div className="lg:col-span-7 relative">
            <motion.div
              style={{ scaleY: lineScale }}
              className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-[#b88457] via-[#b88457]/40 to-transparent origin-top"
            />
            <div className="space-y-12">
              {TIMELINE.map((ev, i) => (
                <Reveal key={i} delay={i * 0.05} className="relative pl-10">
                  <span
                    className={`absolute left-0 top-1.5 w-[15px] h-[15px] border ${
                      ev.danger
                        ? "border-[#c0453a] bg-[#c0453a] shadow-[0_0_16px_rgba(192,69,58,0.7)]"
                        : "border-[#b88457] bg-[#0f1724]"
                    }`}
                  />
                  <div
                    className={`font-mono-data text-xs tracking-[0.2em] mb-2 ${
                      ev.danger ? "text-[#c0453a]" : "text-[#b88457]"
                    }`}
                  >
                    {ev.t}
                  </div>
                  <h3 className="font-display font-bold text-white text-xl md:text-2xl tracking-tight mb-2">
                    {ev.title}
                  </h3>
                  <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-xl">{ev.desc}</p>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Sticky context image */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <div className="relative overflow-hidden border border-white/10">
                  <motion.img
                    style={{ y: imgY }}
                    src={IMAGES.incident}
                    alt="Reactor unit context"
                    className="w-full h-[420px] md:h-[520px] object-cover duotone scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f1724] via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 font-mono-data text-[10px] tracking-[0.2em] text-[#b88457] border border-[#b88457]/40 px-2 py-1 backdrop-blur-sm">
                    CAM-04 · 03:13:58
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 font-mono-data text-[10px] tracking-wider text-zinc-300 flex justify-between">
                    <span>UNIT 4 / V-402 SEPARATOR</span>
                    <span className="text-[#c0453a]">▲ REC</span>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-4 grid grid-cols-3 border border-white/10 divide-x divide-white/10 font-mono-data">
                  {[
                    ["ELAPSED", "16 s"],
                    ["PEAK PRESS", "47.9 bar"],
                    ["INJURIES", "0"],
                  ].map(([k, v]) => (
                    <div key={k} className="p-4">
                      <div className="text-[9px] tracking-[0.2em] text-zinc-500 mb-1">{k}</div>
                      <div className="text-white text-lg">{v}</div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
